import StorageFactory from '@mvps-genesis/storage-factory';

export default class BopisStore {

	/**
	 * List of subscribers to notify updates to
	 *
	 * @var	{Array}
	 */
	#subscribers = [];

	/**
	 * Timeout for localstorage to be considered Stale.
	 *
	 * @var	{Number}	Default: 2 hours
	 */
	#timeout = 120 * 60 * 1000;

	/**
	 * Storage Key for Local Storage
	 *
	 * @var	{String}
	 */
	#localStorageName = 'rh_bopis';

	/**
	 * Constructor of `BopisStore`
	 */
	constructor () {

		/**
		 * State Object
		 *
		 * @var		{Object}
		 * @prop	{Object}	lastSearch		Last search Object
		 * @prop	{Float}		lastSearch.lat	Last search latitude
		 * @prop	{Float}		lastSearch.lng	Last search longitude
		 * @prop	{String}	lastSearch.term	Last search Term (Either City, State from Reverse lookup, or user entered term.)
		 * @prop	{Number}	lastUpdated		Last Updated Timestamp
		 * @prop	{Object}	myStore			"My Store" for the user
		 */
		this.state = {
			lastSearch: {
				lat: null,
				lng: null,
				term: ''
			},
			lastUpdated: Date.now(),
			myStore: {}
		};

		this.#defaultState();
	}

	/**
	 * Returns default state if applicable from local storage
	 */
	#defaultState () {
		const data = this.#getStorage();
		if (data === false) {
			this.#setStorage(this.state);
			return;
		}

		if (data.lastUpdated < Date.now() - this.#timeout) {
			if (data?.myStore?.identifier) {
				this.state.myStore.identifier = data.myStore.identifier;
			}

			this.#setStorage(this.state);
			return;
		}

		this.state = data;
		this.#setStorage(this.state);
	}

	/**
	 * Returns full storage of `bopis`
	 * @returns {Object}
	 */
	#getStorage () {
		return StorageFactory.get(this.#localStorageName);
	}

	/**
	 * Sets value for local storage of `bopis`
	 * @param  {String} value
	 * @returns {Boolean}
	 */
	#setStorage (value) {
		return StorageFactory.set(this.#localStorageName, value);
	}

	/**
	 * Returns the value for a specific key
	 * @param  {String} key
	 * @returns {*} value - String, Object or Array
	 */
	get (key) {
		return this.state[key];
	}

	/**
	 * Sets the value for a specific key
	 * @param  {String} key
	 * @param  {*} value - String, Object or Array
	 * @returns {Boolean}
	 */
	set (key, value) {
		if (!this.state.hasOwnProperty(key)) {
			return false;
		}

		const newState = {...this.state};

		newState[key] = value;
		newState.lastUpdated = Date.now();

		this.state = Object.assign(this.state, newState);

		this.notifySubscribers();
		return this.#setStorage(this.state);
	}

	/**
	 * Notifies #subscribers of an update on the state
	 */
	notifySubscribers () {
		for (let subscriber of this.#subscribers) {
			if (typeof subscriber === 'function') {
				subscriber();
			}
		}
	}

	/**
	 * Unsubscribes a subscriber
	 * @param	{Function}	callback
	 */
	#unsubscribe (callback) {
		this.#subscribers = this.#subscribers.filter(cb => cb !== callback);
	}

	/**
	 * Creates an unsubscriber
	 * @param	{Function}	callback
	 */
	#createUnsubscriber (callback) {
		return () => this.#unsubscribe(callback);
	}

	/**
	 * Subscribes a subscriber
	 * @param	{Function}	callback
	 * @return	{Function}	createUnsubscriber
	 */
	subscribe (callback) {
		this.#subscribers.push(callback);
		return this.#createUnsubscriber(callback);
	}
}
